import { Stack, Theme, SxProps, Typography } from '@mui/material';
import moment from 'moment-timezone';
import { FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import MonthPicker from '@/components/forms/custom-month-picker/custom-month-picker';

type Props = {
  name: string;
  filters: Record<string, any>;
  onChange: (value: SetStateAction<any>) => void;
  label?: string;
  labelFrom?: string;
  labelUntil?: string;
  required?: boolean;
  width?: string | number;
  sx?: SxProps<Theme>;
};

export const MonthRange: FC<Props> = ({
  label,
  labelFrom,
  labelUntil,
  name,
  filters,
  onChange,
  required = false,
  width = '100%',
  sx,
}) => {
  const { t } = useTranslation();
  if (!label) label = t('Month Range');
  if (!labelFrom) labelFrom = t('From');
  if (!labelUntil) labelUntil = t('Until');

  const updateDate = (value: moment.Moment | null, field: 'From' | 'Until') => {
    const fieldName = `${name}${field}`;
    const newValue = field === 'From' ? value?.startOf('month') : value?.endOf('month');

    onChange((prevState: Record<string, any>) => {
      const updatedState = {
        ...prevState,
        page: 0,
        [fieldName]: newValue?.toISOString() ?? '',
        [name]: {
          ...(prevState[name] ?? {}),
          [field.toLowerCase()]: newValue,
        },
      };
      return updatedState;
    });
  };

  const validFrom = moment(filters?.[`${name}From`] || '').isValid();
  const validUntil = moment(filters?.[`${name}Until`] || '').isValid();

  if (required && (!validFrom || !validUntil)) {
    return <Typography color="error">{`MonthRange: ${name} default value is required`}</Typography>;
  }

  const startDate = validFrom
    ? moment(filters?.[name]?.from).utc()
    : moment().subtract(13, 'month').utc().startOf('month');

  let endDate = validUntil ? moment(filters?.[name]?.until).utc() : moment().utc().endOf('month');
  if (startDate > endDate) endDate = startDate.clone().utc().endOf('month');

  const startMinDate = moment('2024-01-01').utc().startOf('year');

  let startMaxDate = startDate.clone().add(13, 'month').utc().endOf('month');
  const endMaxDate = moment().utc().endOf('month');
  if (startMaxDate > endMaxDate) startMaxDate = endMaxDate.clone();

  return (
    <Stack sx={{ width, ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {label}
      </Typography>

      <Stack spacing={2.5} direction="row" sx={{ width: '100%' }}>
        <MonthPicker
          label={labelFrom}
          value={startDate}
          format="MMM YYYY"
          onChange={(newValue) => updateDate(newValue, 'From')}
          minDate={startMinDate}
          maxDate={startMaxDate}
          isValid={validFrom}
          sx={{ flex: 1 }}
        />

        <MonthPicker
          label={labelUntil}
          value={endDate}
          onChange={(newValue) => updateDate(newValue, 'Until')}
          format="MMM YYYY"
          startDate={startDate}
          minDate={startDate}
          maxDate={endMaxDate}
          isValid={validUntil}
          sx={{ flex: 1 }}
        />
      </Stack>
    </Stack>
  );
};
